import FetchError from '../util/fetch-error';
import wrapElements from '../globals/wrap';
import {InitSlider} from '../media/init-slider';

const classes = {
  added: 'is-added',
  animated: 'is-animated',
  disabled: 'is-disabled',
  error: 'has-error',
  loading: 'is-loading',
  open: 'is-open',
  overlayText: 'product-item--overlay-text',
  visible: 'is-visible',
  siblingLinkCurrent: 'sibling__link--current',
  focused: 'is-focused',
};

const settings = {
  errorDelay: 3000,
};

const selectors = {
  animation: '[data-animation]',
  apiContent: '[data-api-content]',
  buttonQuickAdd: '[data-quick-add-btn]',
  buttonAddToCart: '[data-add-to-cart]',
  buttonAddToCartText: '[data-add-to-cart-text]',
  cartDrawer: '[data-cart-drawer]',
  cartLineItems: '[data-line-items]',
  dialog: 'dialog',
  focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
  messageError: '[data-message-error]',
  modalButton: '[data-quick-add-modal-handle]',
  modalContainer: '[data-product-upsell-container]',
  modalContent: '[data-product-upsell-ajax]',
  modalClose: '[data-quick-add-modal-close]',
  productGridItem: 'data-product-grid-item',
  productInformationHolder: '[data-product-information]',
  quickAddHolder: '[data-quick-add-holder]',
  quickAddModal: '[data-quick-add-modal]',
  quickAddModalTemplate: '[data-quick-add-modal-template]',
  gwpProductForm: 'product-form',
};

const attributes = {
  closing: 'closing',
  productId: 'data-product-id',
  modalHandle: 'data-quick-add-modal-handle',
  siblingSwapper: 'data-sibling-swapper',
  quickAddHolder: 'data-quick-add-holder',
  gwpModal: 'data-modal-gwp',
};

class QuickAddProduct extends HTMLElement {
  constructor() {
    super();

    this.container = this;
    this.quickAddHolder = this.container.querySelector(selectors.quickAddHolder);

    if (this.quickAddHolder) {
      this.modal = null;
      this.currentModal = null;
      this.productId = this.quickAddHolder.getAttribute(attributes.quickAddHolder);
      this.modalButton = this.quickAddHolder.querySelector(selectors.modalButton);
      this.handle = this.modalButton?.getAttribute(attributes.modalHandle);
      this.cartDrawer = document.querySelector(selectors.cartDrawer);
      this.buttonQuickAdd = this.quickAddHolder.querySelector(selectors.buttonQuickAdd);
      this.buttonATC = this.quickAddHolder.querySelector(selectors.buttonAddToCart);
      this.button = this.modalButton || this.buttonATC;
      this.modalClose = this.modalClose.bind(this);
      this.modalCloseOnProductAdded = this.modalCloseOnProductAdded.bind(this);
      this.isAnimating = false;
      this.isGwpModal = this.hasAttribute(attributes.gwpModal);
      this.isBundleEdit = this.hasAttribute('data-modal-bundle');
      this.modalButtonClickEvent = this.modalButtonClickEvent.bind(this);
      this.quickAddLoadingToggle = this.quickAddLoadingToggle.bind(this);
    }
  }

  connectedCallback() {
    /**
     * Modal button works for multiple variants products
     */
    if (this.modalButton) {
      this.modalButton.addEventListener('click', this.modalButtonClickEvent);
    }

    /**
     * Quick add button works for single variant products
     */

    if (this.buttonATC) {
      this.buttonATC.addEventListener('click', (e) => {
        e.preventDefault();

        document.dispatchEvent(
          new CustomEvent('theme:cart:add', {
            detail: {
              button: this.buttonATC,
            },
          })
        );
      });
    }

    if (this.quickAddHolder) {
      this.quickAddHolder.addEventListener('animationend', this.quickAddLoadingToggle);
      this.errorHandler();
    }
  }

  modalButtonClickEvent(e) {
    e.preventDefault();

    const isSiblingSwapper = this.modalButton.hasAttribute(attributes.siblingSwapper);
    const isSiblingLinkCurrent = this.modalButton.classList.contains(classes.siblingLinkCurrent);

    if (isSiblingLinkCurrent) return;

    this.modalButton.classList.add(classes.loading);
    this.modalButton.disabled = true;

    // Siblings product modal swapper
    if (isSiblingSwapper && !isSiblingLinkCurrent) {
      this.currentModal = e.target.closest(selectors.quickAddModal);
      this.currentModal.classList.add(classes.loading);
    }

    this.renderModal();
  }

  modalCreate(response) {
    const cachedModal = document.querySelector(`${selectors.quickAddModal}[${attributes.productId}="${this.productId}"]`);

    if (cachedModal && !this.isBundleEdit) {
      this.modal = cachedModal;
      this.modalOpen();
    } else {
      const modalTemplate = this.quickAddHolder.querySelector(selectors.quickAddModalTemplate);
      if (!modalTemplate) return;

      const htmlObject = document.createElement('div');
      htmlObject.innerHTML = modalTemplate.innerHTML;

      // Add dialog to the body
      document.body.appendChild(htmlObject.querySelector(selectors.quickAddModal));
      modalTemplate.remove();

      this.modal = document.querySelector(`${selectors.quickAddModal}[${attributes.productId}="${this.productId}"]`);
      this.modal.querySelector(selectors.modalContent).innerHTML = new DOMParser().parseFromString(response, 'text/html').querySelector(selectors.apiContent).innerHTML;

      this.modalCreatedCallback();
    }
  }

  modalOpen() {
    // Check if browser supports Dialog tags
    if (typeof this.modal.show === 'function') {
      this.modal.show();
    }

    this.modal.setAttribute('open', true);
    this.modal.removeAttribute('inert');

    if (this.currentModal) {
      this.currentModal.dispatchEvent(new CustomEvent('theme:modal:close', {bubbles: false}));
    }

    const focusTarget = this.modal.querySelector('[autofocus]') || this.modal.querySelector(selectors.focusable);
    focusTarget.focus();

    this.quickAddHolder.classList.add(classes.disabled);

    if (this.modalButton) {
      this.modalButton.classList.remove(classes.loading);
      this.modalButton.disabled = false;
    }

    // Animate items
    requestAnimationFrame(() => {
      this.modal.querySelectorAll(selectors.animation).forEach((item) => {
        item.classList.add(classes.animated);
      });
    });

    document.dispatchEvent(new CustomEvent('theme:quick-add:open', {bubbles: true}));
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
    document.addEventListener('theme:product:added', this.modalCloseOnProductAdded, {once: true});
  }

  modalClose() {
    if (this.isAnimating) {
      return;
    }

    if (!this.modal.hasAttribute(attributes.closing)) {
      this.modal.setAttribute(attributes.closing, '');
      this.isAnimating = true;
      return;
    }

    // Check if browser supports Dialog tags
    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
    }

    this.modal.removeAttribute(attributes.closing);
    this.modal.setAttribute('inert', '');
    this.modal.classList.remove(classes.loading);

    if (this.modalButton) {
      this.modalButton.disabled = false;
    }

    if (this.quickAddHolder && this.quickAddHolder.classList.contains(classes.disabled)) {
      this.quickAddHolder.classList.remove(classes.disabled);
    }

    this.resetAnimatedItems();

    const hasOpenDrawer = this.cartDrawer && this.cartDrawer.classList.contains(classes.open);
    const hasOpenModal = document.querySelector('dialog[open]');

    // Unlock scroll if no other drawers & modals are open
    if (!hasOpenDrawer && !hasOpenModal) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }

    document.removeEventListener('theme:product:added', this.modalCloseOnProductAdded);
  }

  modalEvents() {
    // Close button click event
    this.modal.querySelector(selectors.modalClose)?.addEventListener('click', (e) => {
      e.preventDefault();
      this.modalClose();
    });

    // Close dialog on click outside content
    this.modal.addEventListener('click', (event) => {
      if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
        this.modalClose();
      }
    });

    // Close dialog on click ESC key pressed
    this.modal.addEventListener('keydown', (event) => {
      if (event.code == 'Escape') {
        event.preventDefault();
        this.modalClose();
      }
    });

    this.modal.addEventListener('theme:modal:close', () => {
      this.modalClose();
    });

    // Close dialog after animation completes
    this.modal.addEventListener('animationend', (event) => {
      if (event.target !== this.modal) return;
      this.isAnimating = false;

      if (this.modal.hasAttribute(attributes.closing)) {
        this.modalClose();

        // Return focus to quick add button
        if (document.body.classList.contains(classes.focused) && this.buttonQuickAdd) {
          this.buttonQuickAdd.addEventListener(
            'transitionend',
            () => {
              this.buttonQuickAdd.focus();
              this.buttonQuickAdd.classList.remove(classes.visible);
            },
            {once: true}
          );
          this.buttonQuickAdd.classList.add(classes.visible);
        }
      }
    });
  }

  modalCloseOnProductAdded() {
    this.resetQuickAddButtons();
    if (this.modal && this.modal.hasAttribute('open')) {
      this.modalClose();
    }
  }

  quickAddLoadingToggle(e) {
    if (e.target != this.quickAddHolder) return;

    this.quickAddHolder.classList.remove(classes.disabled);
  }

  /**
   * Handle error cart response
   */
  errorHandler() {
    this.quickAddHolder.addEventListener('theme:cart:error', (event) => {
      const holder = event.detail.holder;
      const parentProduct = holder.closest(`[${selectors.productGridItem}]`);
      if (!parentProduct) return;

      const errorMessageHolder = holder.querySelector(selectors.messageError);
      const hasOverlayText = parentProduct.classList.contains(classes.overlayText);
      const productInfo = parentProduct.querySelector(selectors.productInformationHolder);
      const button = holder.querySelector(selectors.buttonAddToCart);

      if (button) {
        button.classList.remove(classes.added, classes.loading);
        holder.classList.add(classes.error);
      }

      if (errorMessageHolder) {
        errorMessageHolder.innerText = event.detail.description;
      }

      if (hasOverlayText) {
        productInfo.classList.add(classes.hidden);
      }

      setTimeout(() => {
        this.resetQuickAddButtons();

        if (hasOverlayText) {
          productInfo.classList.remove(classes.hidden);
        }
      }, settings.errorDelay);
    });
  }

  /**
   * Reset buttons to default states
   */
  resetQuickAddButtons() {
    if (this.quickAddHolder) {
      this.quickAddHolder.classList.remove(classes.visible, classes.error);
    }

    if (this.buttonQuickAdd) {
      this.buttonQuickAdd.classList.remove(classes.added);
      this.buttonQuickAdd.disabled = false;
    }
  }

  renderModal() {
    if (this.modal) {
      this.modalOpen();
    } else {
      window
        .fetch(`${window.theme.routes.root}products/${this.handle}?section_id=api-product-upsell`)
        .then(this.upsellErrorsHandler)
        .then((response) => {
          return response.text();
        })
        .then((response) => {
          this.modalCreate(response);
        });
    }
  }

  modalCreatedCallback() {
    this.modalEvents();
    this.modalOpen();

    new InitSlider(null, this.modal);

    wrapElements(this.modal);

    if (this.isGwpModal) this.updateGwpModal()

    if (this.isBundleEdit) this.initBundleEditModal()
  }

  initBundleEditModal() {
    const bundleData = JSON.parse(this.getAttribute('data-modal-bundle'));
    const bundleItems = Array.from(this.modal.querySelectorAll('bundle-item'));
    const productForm = this.modal.querySelector(selectors.gwpProductForm);
    const removeBundleData = this.getAttribute('data-bundle-remove');
    const bundleDataQuantity = this.getAttribute('data-bundle-quantity');

    Object.keys(bundleData).forEach((key, index) => {
      const color = bundleData[key]
      const swatch = bundleItems[index].querySelector(`input[value="${color}"] + label`);

      if (swatch) swatch.click();
    });
    productForm.setAttribute('data-bundle-edit-form', removeBundleData);
    productForm.setAttribute('data-bundle-edit-form-quantity', bundleDataQuantity);
  }

  upsellErrorsHandler(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }

  resetAnimatedItems() {
    this.modal?.querySelectorAll(selectors.animation).forEach((item) => {
      item.classList.remove(classes.animated);
    });
  }

  updateGwpModal() {
    const submitBtnText = this.modal.querySelector(selectors.buttonAddToCartText);
    const gwpColorOption = this.getAttribute(attributes.gwpModal);
    const productForm = this.modal.querySelector(selectors.gwpProductForm);
    const gwpId = document.querySelector('[data-item-gwp-id]')?.getAttribute('data-item-gwp-id');

    if (gwpColorOption) {
      const modalOptionTarget = this.modal.querySelector(`input[value="${gwpColorOption}"] + label`);
      if (modalOptionTarget) modalOptionTarget.click();
    }

    submitBtnText.textContent = theme.strings.changeOptionGwp;
    submitBtnText.setAttribute('data-change-gwp-text', 'true');
    productForm.setAttribute('data-gwp-change-form', gwpId);
    productForm.querySelector('form').insertAdjacentHTML('afterbegin', '<input type="hidden" name="properties[_gwp_product]" value="true">')
  }
}

export {QuickAddProduct};
